import type { ReactNode } from "react";
import { formatarPreco, type CatalogoDoLaboratorio } from "@bookdental/schemas";

/** Ação no fim de uma linha (o "Pedir" do link público); nunca sai no PDF. */
export type AcaoDaLinha = (linha: { servicoId: string; linhaId: string }) => ReactNode;

/** Cabeçalho do laboratório e os serviços por categoria; a cor de destaque só nos títulos e
 *  traços — a lista fica em cinza, pra ler. */
export function ListaDoCatalogo({
  catalogo,
  destaque,
  comCor,
  acaoDaLinha,
}: {
  catalogo: CatalogoDoLaboratorio;
  destaque: string;
  comCor: boolean;
  acaoDaLinha?: AcaoDaLinha;
}) {
  const { laboratorio: lab, categorias, comPreco } = catalogo;
  const { mostrar, rodapeTexto } = catalogo.aparencia;
  const contato = [
    lab.telefone,
    mostrar.cnpj && lab.cnpj ? `CNPJ ${lab.cnpj}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const total = categorias.reduce((n, c) => n + c.servicos.length, 0);
  const preco = (cents: number | null) =>
    cents === null ? (
      <span className="font-normal text-zinc-400">sob consulta</span>
    ) : (
      formatarPreco(cents)
    );

  return (
    <>
      <header
        className={`flex items-center gap-4 pb-5 ${comCor ? "border-b-2" : "border-b border-zinc-200"}`}
        style={comCor ? { borderColor: destaque } : undefined}
      >
        {lab.logoUrl && (
          <img
            src={lab.logoUrl}
            alt=""
            className="h-16 w-16 shrink-0 rounded-lg object-contain"
          />
        )}
        <div className="min-w-0 flex-1">
          <h1
            className="text-2xl font-bold leading-tight"
            style={comCor ? { color: destaque } : undefined}
          >
            {lab.nome}
          </h1>
          <p className="mt-0.5 text-sm text-zinc-500">Catálogo de serviços</p>
          {contato && <p className="mt-1 text-xs text-zinc-500">{contato}</p>}
          {mostrar.endereco && lab.endereco && (
            <p className="text-xs text-zinc-500">{lab.endereco}</p>
          )}
        </div>
      </header>
      {/* Só no PDF: as notas do fim vêm pra cá (ver RodapeDoCatalogo). */}
      {(rodapeTexto || comPreco) && (
        <div className="mt-3 hidden space-y-0.5 text-[11px] text-zinc-500 print:block">
          {rodapeTexto && <p className="whitespace-pre-line text-zinc-600">{rodapeTexto}</p>}
          {comPreco && <p>Preços da tabela base do laboratório; podem variar por cliente.</p>}
        </div>
      )}

      {total === 0 ? (
        <p className="py-10 text-center text-sm text-zinc-500">
          Nenhum serviço oferecido no momento.
        </p>
      ) : (
        <div className="mt-5 space-y-6">
          {categorias.map(({ categoria, servicos }) => (
            <section key={categoria} className="break-inside-avoid">
              <h2
                className="mb-1.5 border-b-2 pb-1 text-sm font-bold uppercase tracking-wide"
                style={{
                  borderColor: destaque,
                  color: comCor ? destaque : undefined,
                }}
              >
                {categoria}
              </h2>
              <div className="divide-y divide-zinc-100">
                {servicos.map((servico) => {
                  // O que a aparência esconde some aqui também: a pré-visualização do editor muda na hora.
                  const s = {
                    ...servico,
                    imagemUrl: mostrar.fotos ? servico.imagemUrl : null,
                    descricao: mostrar.descricoes ? servico.descricao : null,
                  };
                  return (
                    <div key={s.id} className="break-inside-avoid py-1.5">
                      {/* Com foto ou descrição, o serviço ganha um cabeçalho; as linhas por material vêm embaixo. */}
                      {(s.imagemUrl || s.descricao) && (
                        <div className="flex items-start gap-3 pb-1 pt-1">
                          {s.imagemUrl && (
                            <img
                              src={s.imagemUrl}
                              alt=""
                              className="h-14 w-14 shrink-0 rounded-md border border-zinc-100 object-cover"
                            />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">{s.label}</p>
                            {s.descricao && (
                              <p className="text-xs leading-snug text-zinc-500">
                                {s.descricao}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      <ul>
                        {s.linhas.map((l) => (
                          <li
                            key={l.id}
                            className="flex items-baseline gap-3 py-1 text-sm"
                          >
                            <span className="min-w-0 flex-1">
                              {s.label}
                              {l.material && (
                                <span className="font-medium">
                                  {" "}
                                  {l.material}
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-xs text-zinc-400">
                              {s.abrangencia === "ARCADA"
                                ? "por arcada"
                                : "por dente"}
                            </span>
                            {comPreco && (
                              <span className="w-32 shrink-0 text-right font-semibold tabular-nums">
                                {preco(l.precoCents)}
                              </span>
                            )}
                            {acaoDaLinha && (
                              <span className="shrink-0 print:hidden">
                                {acaoDaLinha({ servicoId: s.id, linhaId: l.id })}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
